// import { useHistory, useLocation } from "react-router";
import useFilters from "./useFilters";
import { useContext, useMemo } from "react";
import { ResultsContext } from "src/context/resultsContext/context";
import { getSearchParams, sortAlphabeticalOrder } from "src/utils/generalUtils";
import { Bus, Categories, ResultsData } from "../types";
import { cloneDeep } from "lodash";
import {
  generatListForFilters,
  getCheapestFare,
  getCheapestGovtFare,
  getGovtBusesList,
  getNormalBuses,
  getTotalRTCBuses,
} from "../utils";
import { GovtBuses } from "src/context/resultsContext/types";
import { isEmpty, isWindowPresent } from "src/utils/browserHelpers";
import { useIsAnyFilterSelected } from "./useIsAnyFilterSelected";
import { getUTMData } from "src/utils/uiHelpers";
import { sendBusPageLoad } from "src/analytics/useAnalytics";
// import { sendToGTM } from "src/analytics/gtmGA";
import dayjs from "dayjs";
import { usePathname } from "next/navigation";

const useResultsUtils = (fromCityName: string, toCityName: string) => {
  // const history = useHistory();
  const { handleClearFilters } = useFilters();
  const {
    selectedFilters,
    setListOptions,
    resultsData,
    setResultsData,
    noResultsFound,
    setNoResultsFound,
    setGovtBuses,
    govtBuses,
  } = useContext(ResultsContext);
  const filterListFromSS = isWindowPresent
    ? JSON.parse(sessionStorage.getItem("filterList") as string)
    : null;
  const pathName = usePathname();
  const { journeyDate, solutionId } = getSearchParams();

  const { selectedFiltersList, selectedSort } = useIsAnyFilterSelected();
  // const location = useLocation();

  const goBack = () => {
    // history.push("/bus");
  };

  const onClickForNoData = (type: string) => {
    switch (type) {
      case "NO_DATA_FOUND":
        return goBack;

      case "FILTER_ERROR":
        return handleClearFilters;

      case "INCORRECT_DEPART_DATE":
      case "INCORRECT_URL":
      case "API_FAILED":
        return goBack;
    }
  };

  const onClickTitleForNoData = (type: string) => {
    switch (type) {
      // case "NO_DATA_FOUND":
      //   return "Try again";

      case "FILTER_ERROR":
        return "Clear filters";

      // case "API_FAILED":
      //   return "Try again";
    }
  };

  // generating the list for amenities, bus operators, pickup points and dropoff points
  const handleListOptionsData = (busData: Bus[]) => {
    const selectedFiltersFromSS = isWindowPresent
      ? sessionStorage.getItem("selectedFilters") !== null
        ? JSON.parse(sessionStorage.getItem("selectedFilters") as string)
        : null
      : null;

    const { amenities, pickupPoints, dropOffPoints, busOperators } =
      generatListForFilters(
        busData,
        selectedFiltersFromSS
          ? selectedFiltersFromSS // take list from session storahe if we have to reatian filters
          : selectedFilters
      );

    setListOptions({
      amenities: amenities.sort(sortAlphabeticalOrder),
      pickupPoints: pickupPoints.sort(sortAlphabeticalOrder),
      dropOffPoints: dropOffPoints.sort(sortAlphabeticalOrder),
      busOperators: busOperators.sort(sortAlphabeticalOrder),
    });
  };

  function handlePathNameChart(
    buses: Bus[],
    categories: Categories[],
    solutionId: string,
    response: Record<string, any>,
    govtBusList: GovtBuses | []
  ) {
    const normalBusList = categories?.length
      ? getNormalBuses(buses, categories)
      : buses;

    const searchedBusIndex = normalBusList.findIndex(
      (item) => item.solutionId === solutionId
    );

    if (searchedBusIndex > 0) {
      const [removedBus] = normalBusList.splice(searchedBusIndex, 1);
      normalBusList.unshift(removedBus);
      response.data.buses = normalBusList;
      setGovtBuses(categories?.length ? (govtBusList as GovtBuses) : {});
      setResultsData(cloneDeep(response.data));
      return true;
    }

    return false;
  }

  const handleAPIResponse = (response: Record<string, any>) => {
    if (noResultsFound?.value) {
      setNoResultsFound({
        type: { TYPE: "", TITLE: "", MESSAGE: "" },
        value: false,
      });
    }
    handleListOptionsData(response.data.buses);
    const categories: Categories[] = response.data.categories;
    const buses: Bus[] = cloneDeep(response.data.buses);
    const govtBusList =
      categories?.length > 0 ? getGovtBusesList(buses, categories) : [];

    //handling code for deep link for chart
    if (
      pathName?.includes("chart") &&
      handlePathNameChart(buses, categories, solutionId, response, govtBusList)
    ) {
      return;
    }

    const normalBuses = categories?.length
      ? getNormalBuses(buses, categories)
      : buses;
    response.data.buses = normalBuses;
    setGovtBuses(categories?.length ? (govtBusList as GovtBuses) : {});
    setResultsData(cloneDeep(response.data));
  };

  const getFromCity = useMemo(() => {
    if (fromCityName && fromCityName !== "undefined") return fromCityName;
    return resultsData?.sc?.fromCityName;
  }, [fromCityName, resultsData?.sc]);

  const getToCity = useMemo(() => {
    if (toCityName && toCityName !== "undefined") return toCityName;
    return resultsData?.sc?.toCityName;
  }, [toCityName, resultsData?.sc]);

  const getHeaderTitle = useMemo(() => {
    if (
      fromCityName &&
      toCityName &&
      fromCityName !== "undefined" &&
      toCityName !== "undefined"
    )
      return `${fromCityName} to ${toCityName}`;
    else if (resultsData?.sc) {
      return `${resultsData?.sc?.fromCityName} to ${resultsData?.sc?.toCityName}`;
    } else return "";
  }, [fromCityName, resultsData, toCityName]);

  const triggerPageLoadEvent = (
    resultsData: ResultsData,
    isSEOPage = false
  ) => {
    const miniPricePvtBus = getCheapestFare(cloneDeep(resultsData.buses));
    const miniPriceGovtBus = !isEmpty(govtBuses)
      ? getCheapestGovtFare(govtBuses)
      : 0;
    const filtersApplied = selectedFiltersList();
    const sortApp = selectedSort ? selectedSort?.toString() : "NA";
    const dx = Math.ceil(dayjs(journeyDate).diff(dayjs(), "day", true));
    const { utm_source, utm_medium, utm_term, utm_campaign } = getUTMData();

    const actionData = {
      pageName: "Bus_Searchresults_Page",
      sourceCity: resultsData?.sc?.fromCityName,
      destinationCity: resultsData?.sc?.toCityName,
      fromCityId: resultsData?.sc?.fromCityId,
      toCityId: resultsData?.sc?.toCityId,
      travelDate: journeyDate,
      numberOfSearchResults: resultsData.buses.length,
      numberOfRTCBuses: getTotalRTCBuses(govtBuses),
      minPriceOfPrivateBus: miniPricePvtBus,
      minPriceOfRTCBus: miniPriceGovtBus,
      hasAc: filtersApplied?.busType?.includes("non_ac") ? true : false,
      hasNonAc: filtersApplied?.busType?.includes("hasAc") ? true : false,
      hasSeater: filtersApplied?.seatType?.includes("isSeater") ? true : false,
      hasSleeper: filtersApplied?.seatType?.includes("isSleeper")
        ? true
        : false,
      hasStarRating: filtersApplied?.starRating
        ? filtersApplied?.starRating?.toString()
        : false,
      hasPickupPoints: filtersApplied?.pickupPoints ? true : false,
      hasDroppingPoints: filtersApplied?.dropOffPoints ? true : false,
      hasAmenities: filtersApplied?.amenities
        ? filtersApplied?.amenities?.toString()
        : false,
      hasOperators: filtersApplied?.busOperators
        ? filtersApplied?.busOperators?.toString()
        : false,
      sortApplied: sortApp,
      searchId: resultsData?.sc?.searchId,
      seoPage: isSEOPage,
      dx,
      utm_source,
      utm_campaign,
      utm_medium,
      utm_term,
    };
    sendBusPageLoad("BUS_Page_Load", actionData, "b_srp_pageload");
    // sendToGTM(actionData, "BUS_Page_Load");
  };

  return {
    onClickForNoData,
    onClickTitleForNoData,
    handleListOptionsData,
    filterListFromSS,
    handleAPIResponse,
    getFromCity,
    getToCity,
    getHeaderTitle,
    triggerPageLoadEvent,
  };
};

export default useResultsUtils;
